import {
  Body,
  Caption,
  Card,
  VAlert,
  VButton,
  VDialog,
  VTextarea,
  cn,
} from '@vrzl/ui';
import { CheckCircle2, Lock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { getSelectedTenantLabel, useAuth } from '@/domains/auth';

import { useSubmitFeedback } from '../api/mutations';
import { MAX_FEEDBACK_LENGTH, counterTone } from '../lib/counterTone';
import { organisationLabel } from '../lib/organisationLabel';

const ERROR_MESSAGE = 'De feedback kon niet verstuurd worden. Probeer het opnieuw.';
const CLOSE_DELAY_MS = 1500;

type FeedbackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutate, isPending, isError, reset } = useSubmitFeedback();

  const closeAndReset = useCallback(() => {
    setMessage('');
    setIsSubmitted(false);
    reset();
    onOpenChange(false);
  }, [onOpenChange, reset]);

  // Na succes sluit de modal vanzelf; de cleanup ruimt de timer op wanneer de
  // gebruiker eerder zelf sluit.
  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(closeAndReset, CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isSubmitted, closeAndReset]);

  if (!user) return null;

  const subLabel = organisationLabel(user.activePerspective?.name, getSelectedTenantLabel());

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending) return;
    if (!nextOpen) {
      closeAndReset();
      return;
    }
    onOpenChange(true);
  };

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    mutate(
      { message: trimmed, route: location.pathname + location.search },
      { onSuccess: () => setIsSubmitted(true) },
    );
  };

  return (
    <VDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      title="Feedback geven"
      description="Laat ons weten wat beter kan."
      headerLayout="stacked"
      footer={
        isSubmitted ? undefined : (
          <div className="flex justify-end gap-2">
            <VButton variant="ghost" onClick={() => handleOpenChange(false)} disabled={isPending}>
              Annuleren
            </VButton>
            <VButton onClick={handleSubmit} disabled={!message.trim() || isPending}>
              {isPending ? 'Versturen…' : 'Versturen'}
            </VButton>
          </div>
        )
      }
    >
      {isSubmitted ? (
        <div className="flex flex-col items-center gap-2 px-4 py-8">
          <CheckCircle2 className="size-8 text-success-600" aria-hidden="true" />
          <Body weight="medium">Bedankt voor je feedback!</Body>
        </div>
      ) : (
        <div className="space-y-4 px-4 py-3">
          {/* Identiteitsblok — read-only, komt uit de sessie (AC-5) */}
          <Card className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-neutral-200">
              <Body as="span" weight="semibold">
                {user.initials}
              </Body>
            </div>
            <div className="min-w-0 flex-1">
              <Body weight="medium">{user.name}</Body>
              {subLabel && <Caption className="truncate">{subLabel}</Caption>}
            </div>
            <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Komt uit je account, niet aanpasbaar</span>
          </Card>

          <div>
            <VTextarea
              label="Je feedback"
              placeholder="Beschrijf je feedback, idee of probleem…"
              rows={6}
              maxLength={MAX_FEEDBACK_LENGTH}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isPending}
              className="h-36 min-h-0 resize-none overflow-y-auto"
            />
            <Caption
              as="p"
              aria-live="polite"
              className={cn('mt-1.5 text-right', counterTone(message.length))}
            >
              {message.length}/{MAX_FEEDBACK_LENGTH}
            </Caption>
          </div>

          {isError && <VAlert variant="error">{ERROR_MESSAGE}</VAlert>}
        </div>
      )}
    </VDialog>
  );
}
